<template>
  <el-row :gutter="12" class="charts-section">
    <!-- Posts by Day Chart -->
    <el-col :xs="24" :sm="24" :md="16" :lg="16">
      <el-card shadow="never" class="chart-card" :body-style="{ padding: '14px' }">
        <template #header>
          <div class="chart-header">
            <el-icon><TrendCharts /></el-icon>
            <span>Постов за день</span>
          </div>
        </template>
        <v-chart v-if="chartData" class="chart" :option="dailyChartOption" autoresize />
        <el-empty v-else description="Загрузка..." />
      </el-card>
    </el-col>

    <!-- Category Distribution Chart -->
    <el-col :xs="24" :sm="24" :md="8" :lg="8">
      <el-card shadow="never" class="chart-card" :body-style="{ padding: '14px' }">
        <template #header>
          <div class="chart-header">
            <el-icon><PieChart /></el-icon>
            <span>Категории</span>
          </div>
        </template>
        <v-chart v-if="chartData" class="chart" :option="categoryChartOption" autoresize />
        <el-empty v-else description="Загрузка..." />
      </el-card>
    </el-col>
  </el-row>

  <!-- Federal District Chart -->
  <el-row :gutter="12" class="charts-section" style="margin-top: 12px">
    <el-col :span="24">
      <el-card shadow="never" class="chart-card district-chart" :body-style="{ padding: '14px' }">
        <template #header>
          <div class="chart-header">
            <el-icon><MapLocation /></el-icon>
            <span>Распределение по федеральным округам</span>
          </div>
        </template>
        <v-chart v-if="chartData" class="chart district-chart-inner" :option="districtChartOption" autoresize />
        <el-empty v-else description="Загрузка..." />
      </el-card>
    </el-col>
  </el-row>
</template>

<script setup lang="ts">
// @ts-nocheck
import { ref, computed, onMounted, watch } from 'vue'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, PieChart as EChartsPieChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import { fetchChartStats } from '@/api/client'
import type { ChartStats } from '@/types'
import { useThemeStore } from '@/stores/theme'

use([
  CanvasRenderer,
  BarChart,
  EChartsPieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
])

const props = defineProps<{
  dateFrom?: string | null
  dateTo?: string | null
}>()

const chartData = ref<ChartStats | null>(null)
const themeStore = useThemeStore()
const chartTheme = computed(() => {
  const dark = themeStore.theme === 'dark'
  return {
    text: dark ? '#c8cdd7' : '#606266',
    muted: dark ? '#9aa1ad' : '#909399',
    line: dark ? '#343946' : '#e4e7ed',
    tooltipBackground: dark ? '#222630' : '#ffffff',
    pieBorder: dark ? '#181b22' : '#ffffff',
  }
})

async function loadChartData() {
  try {
    chartData.value = await fetchChartStats(props.dateFrom ?? undefined, props.dateTo ?? undefined)
  } catch (e) {
    console.error('Failed to load chart stats:', e)
  }
}

onMounted(() => {
  loadChartData()
})

// Reload when date range changes
watch(
  () => [props.dateFrom, props.dateTo],
  () => {
    loadChartData()
  },
  { deep: true }
)

const dailyChartOption = computed(() => {
  const data = chartData.value
  if (!data) return {}
  const colors = chartTheme.value
  return {
    textStyle: { color: colors.text },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      textStyle: { fontSize: 14 },
      backgroundColor: colors.tooltipBackground,
      borderColor: colors.line,
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: data.posts_by_day.map((d) => d.name),
      axisLabel: { rotate: 30, fontSize: 13, fontWeight: 'bold', color: colors.text },
      axisLine: { lineStyle: { color: colors.line } },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { fontSize: 13, fontWeight: 'bold', color: colors.text },
      splitLine: { lineStyle: { color: colors.line } },
    },
    series: [
      {
        name: 'Постов',
        data: data.posts_by_day.map((d) => d.count),
        type: 'bar',
        itemStyle: {
          color: '#409eff',
          borderRadius: [4, 4, 0, 0],
        },
        barWidth: '60%',
      },
    ],
  }
})

const categoryColors: Record<string, string> = {
  abuse: '#f56c6c',
  conditions: '#e6a23c',
  medical: '#409eff',
  payment: '#67c23a',
  equipment: '#9254de',
  command: '#909399',
  mobilization: '#a0cfff',
  rights: '#79bbff',
  mental_health: '#f89898',
  unclear: '#c8c9cc',
  communication: '#909399',
  casualties: '#f56c6c',
  training: '#67c23a',
  corruption: '#e6a23c',
  family: '#409eff',
  logistics: '#a0cfff',
  discrimination: '#9254de',
}

const categoryLabels: Record<string, string> = {
  abuse: 'Дедовщина',
  conditions: 'Условия службы',
  medical: 'Медицина',
  payment: 'Зарплата/выплаты',
  equipment: 'Снаряжение',
  command: 'Командование',
  mobilization: 'Мобилизация',
  rights: 'Права военных',
  mental_health: 'Псих. здоровье',
  unclear: 'Неясно',
  communication: 'Коммуникация',
  casualties: 'Потери',
  training: 'Подготовка',
  corruption: 'Коррупция',
  family: 'Семья',
  logistics: 'Логистика',
  discrimination: 'Дискриминация',
}

const categoryChartOption = computed(() => {
  const data = chartData.value
  if (!data) return {}
  const colors = chartTheme.value
  return {
    textStyle: { color: colors.text },
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)',
      textStyle: { fontSize: 14 },
      backgroundColor: colors.tooltipBackground,
      borderColor: colors.line,
    },
    series: [
      {
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 4,
          borderColor: colors.pieBorder,
          borderWidth: 2,
        },
        label: { show: false },
        emphasis: {
          label: {
            show: true,
            fontSize: 16,
            fontWeight: 'bold',
          },
        },
        data: data.categories.map((item) => ({
          name: categoryLabels[item.name] || item.name,
          value: item.count,
          itemStyle: { color: categoryColors[item.name] || '#c8c9cc' },
        })),
      },
    ],
  }
})

const districtColors: Record<string, string> = {
  'ЦФО': '#e74c3c',
  'СЗФО': '#3498db',
  'ЮФО': '#f39c12',
  'СКФО': '#9b59b6',
  'ПФО': '#1abc9c',
  'УрФО': '#e67e22',
  'СФО': '#2ecc71',
  'ДФО': '#34495e',
  'НЕОПР': '#95a5a6',
}

const districtChartOption = computed(() => {
  const data = chartData.value
  if (!data) return {}
  const colors = chartTheme.value
  return {
    textStyle: { color: colors.text },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      textStyle: { fontSize: 14 },
      backgroundColor: colors.tooltipBackground,
      borderColor: colors.line,
      formatter: '{b}: {c} постов',
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: data.districts.map((d) => d.name),
      axisLabel: { fontSize: 13, fontWeight: 'bold', color: colors.text },
      axisLine: { lineStyle: { color: colors.line } },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { fontSize: 13, fontWeight: 'bold', color: colors.text },
      splitLine: { lineStyle: { color: colors.line } },
    },
    series: [
      {
        data: data.districts.map((item) => ({
          value: item.count,
          itemStyle: { color: districtColors[item.name] || '#c8c9cc' },
        })),
        type: 'bar',
        barWidth: '60%',
        itemStyle: { borderRadius: [4, 4, 0, 0] },
      },
    ],
  }
})
</script>

<style scoped>
.charts-section {
  margin-bottom: 16px;
}

.chart-card {
  height: 340px;
}

.chart-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--app-text-primary);
}

.chart {
  height: 270px;
  width: 100%;
}

.district-chart {
  height: 280px;
}

.district-chart-inner {
  height: 210px;
}
</style>
